/** Same-origin Web settings routes for Grok Build OAuth. */

import type { ServerResponse } from "node:http";
import type { Context } from "@deepseek-ai/cordis";
import type {} from "@deepseek-ai/dsh-host-webserver";
import type {} from "@deepseek-ai/dsh-llm";
import type { AuthEvent, AuthPrompt } from "@earendil-works/pi-ai";
import {
	type AccountSummary,
	CODING_OAUTH_ACCOUNTS_REMOVE_PATH,
	CODING_OAUTH_ACCOUNTS_SET_ACTIVE_PATH,
} from "../../shared/coding-oauth.js";
import { CODING_OAUTH_CORE_ABI, type DshCompatibility } from "../../shared/compatibility.js";
import { grokBuildAuthStatus, importGrokBuildSession, loginGrokBuildSession } from "./auth.js";
import { authorizeCodingOAuthRequest } from "./authorize-request.js";
import type { CatalogSource } from "./catalog.js";
import { probeGrokAuth } from "./grok-import.js";
import { readJsonRequest, requestErrorStatus } from "./http-json.js";
import {
	ANTIGRAVITY_ROUTE,
	CODING_OAUTH_LOGIN_CANCEL_PATH,
	CODING_OAUTH_LOGIN_CODE_PATH,
	CODING_OAUTH_LOGIN_PATH,
	CODING_OAUTH_LOGOUT_PATH,
	CODING_OAUTH_MODELS_PATH,
	CODING_OAUTH_STATUS_PATH,
	type CodingOAuthProviderSlug,
	GROK_BUILD_AUTH_IMPORT_PATH,
	GROK_BUILD_AUTH_LOGIN_CANCEL_PATH,
	GROK_BUILD_AUTH_LOGIN_CODE_PATH,
	GROK_BUILD_AUTH_LOGIN_PATH,
	GROK_BUILD_AUTH_LOGOUT_PATH,
	GROK_BUILD_AUTH_MODELS_PATH,
	GROK_BUILD_AUTH_STATUS_PATH,
} from "./ids.js";
import { loginGrokBuildPkce } from "./oauth.js";
import type { SubscriptionLoginMethod } from "./oauth-providers.js";
import type { OAuthProviderSession } from "./oauth-session.js";
import { safeMessage } from "./redact.js";
import type { GrokBuildSession } from "./session.js";
import type { LoginPersistOptions, OAuthCredentialFileStore } from "./store.js";
import { LOOPBACK_OWNER_REQUEST_POLICY, type OwnerAccessMode, type OwnerRequestPolicy } from "./web-origin.js";
import { registerWebRouteSetupAtomically } from "./web-routes.js";

export {
	CODING_OAUTH_LOGIN_CANCEL_PATH,
	CODING_OAUTH_LOGIN_CODE_PATH,
	CODING_OAUTH_LOGIN_PATH,
	CODING_OAUTH_LOGOUT_PATH,
	CODING_OAUTH_MODELS_PATH,
	CODING_OAUTH_STATUS_PATH,
	GROK_BUILD_AUTH_IMPORT_PATH,
	GROK_BUILD_AUTH_LOGIN_CANCEL_PATH,
	GROK_BUILD_AUTH_LOGIN_CODE_PATH,
	GROK_BUILD_AUTH_LOGIN_PATH,
	GROK_BUILD_AUTH_LOGOUT_PATH,
	GROK_BUILD_AUTH_MODELS_PATH,
	GROK_BUILD_AUTH_STATUS_PATH,
} from "./ids.js";

export type GrokBuildLoginMethod = "pkce" | "device";

export type GrokBuildWebAuthStatus =
	| { status: "signed-out"; grokImportAvailable: boolean }
	| {
			status: "signing-in";
			method: GrokBuildLoginMethod;
			url?: string;
			userCode?: string;
			grokImportAvailable: boolean;
	  }
	| {
			status: "signed-in";
			models: string[];
			available: string[];
			selected: string[];
			selectionMode?: "default" | "selected";
			catalogSource: CatalogSource;
			catalogError?: string;
			grokImportAvailable: boolean;
			accounts: AccountSummary[];
			activeAccountId: string;
	  }
	| { status: "error"; message: string; grokImportAvailable: boolean };

export interface LoginChallenge {
	method: GrokBuildLoginMethod;
	url: string;
	userCode?: string;
}

function waitForPromptAbort(prompt: AuthPrompt): Promise<string> {
	const signal = prompt.signal;
	if (signal === undefined) return new Promise<string>(() => {});
	if (signal.aborted) return Promise.reject(signal.reason);
	return new Promise<string>((_resolve, reject) => {
		signal.addEventListener(
			"abort",
			() => {
				reject(signal.reason);
			},
			{ once: true },
		);
	});
}

async function grokImportAvailable(): Promise<boolean> {
	return (await probeGrokAuth()).available;
}

async function signedInAccountFields(store: OAuthCredentialFileStore): Promise<{
	accounts: AccountSummary[];
	activeAccountId: string;
}> {
	const accounts = [...(await store.listAccounts())];
	const activeAccountId = await store.getActiveAccountId();
	if (activeAccountId === undefined || accounts.length === 0) {
		throw new Error("signed-in status requires at least one stored account");
	}
	return { accounts, activeAccountId };
}

function readLoginPersistOptions(body: Record<string, unknown>): LoginPersistOptions {
	const mode = body["accountMode"];
	if (mode !== undefined && !["add", "overwrite-active", "reauthorize"].includes(String(mode)))
		throw new TypeError("invalid account mode");
	if (mode === "reauthorize" && (typeof body["targetAccountId"] !== "string" || !body["targetAccountId"]))
		throw new TypeError("targetAccountId is required");
	return {
		mode: mode === "reauthorize" || mode === "overwrite-active" ? mode : "add",
		...(typeof body["targetAccountId"] === "string" ? { targetAccountId: body["targetAccountId"] } : {}),
		...(body["confirmOverwrite"] === true ? { confirmOverwrite: true } : {}),
	};
}

/**
 * One lifecycle owner for the pending login (PKCE or device), the published
 * challenge, the pasted-code channel, and the public status.
 */
export class GrokBuildWebAuth {
	private state: GrokBuildWebAuthStatus = { status: "signed-out", grokImportAvailable: false };
	private operation: Promise<void> | undefined;
	private lastLoginError: string | undefined;
	private cancellation: AbortController | undefined;
	private method: GrokBuildLoginMethod = "pkce";
	private loginPersist: LoginPersistOptions = { mode: "add" };
	private challenge: LoginChallenge | undefined;
	private challengeWaiters: Array<{ resolve(value: LoginChallenge): void; reject(error: unknown): void }> = [];
	private codeResolver: ((code: string) => void) | undefined;

	constructor(private readonly session: GrokBuildSession) {}

	async status(): Promise<GrokBuildWebAuthStatus & { operationError?: string }> {
		if (this.operation !== undefined && this.state !== undefined) return this.state;
		const stored = await this.readStoredStatus();
		return this.lastLoginError === undefined ? stored : { ...stored, operationError: this.lastLoginError };
	}

	/** Start (or join) a login. A different method aborts and restarts the flow. */
	async signIn(method: GrokBuildLoginMethod, persist: LoginPersistOptions = { mode: "add" }): Promise<LoginChallenge> {
		if (this.operation !== undefined && this.method !== method) {
			await this.cancel();
		}
		if (
			this.operation !== undefined &&
			(this.loginPersist.mode !== persist.mode || this.loginPersist.targetAccountId !== persist.targetAccountId)
		)
			throw new Error("login already in progress for another account; cancel it first");
		if (this.operation === undefined) {
			this.loginPersist = { ...persist };
			this.start(method);
		}
		if (this.challenge !== undefined) return this.challenge;
		return new Promise<LoginChallenge>((resolve, reject) => {
			this.challengeWaiters.push({ resolve, reject });
		});
	}

	/** Hand a pasted authorization code (or redirect URL) to a pending PKCE login. */
	async submitCode(code: string): Promise<void> {
		const resolver = this.codeResolver;
		if (resolver === undefined) {
			throw new Error("grok-build: no authorization-code login is waiting for a code");
		}
		this.codeResolver = undefined;
		resolver(code);
	}

	/** Abort a pending login without touching any stored credential. */
	async cancel(): Promise<void> {
		this.cancellation?.abort(new Error("grok-build: sign-in cancelled"));
		await this.operation?.catch(() => undefined);
		this.lastLoginError = undefined;
		this.codeResolver = undefined;
		this.challenge = undefined;
		this.state = await this.readStoredStatus();
	}

	async importGrok(): Promise<void> {
		this.cancellation?.abort(new Error("grok-build: sign-in cancelled"));
		await this.operation?.catch(() => undefined);
		this.lastLoginError = undefined;
		this.codeResolver = undefined;
		await importGrokBuildSession(this.session);
		this.challenge = undefined;
		this.state = await this.readStoredStatus();
	}

	async setModels(ids: readonly string[] | undefined): Promise<void> {
		await this.session.setSelectedModels(ids);
		this.state = await this.readStoredStatus();
	}

	async setActiveAccount(id: string): Promise<void> {
		await this.session.store.setActiveAccount(id);
		this.session.notifyCredentialChange();
		this.state = await this.readStoredStatus();
	}

	async removeAccount(id: string): Promise<void> {
		await this.session.store.removeAccount(id);
		this.session.notifyCredentialChange();
		this.state = await this.readStoredStatus();
	}

	async signOut(): Promise<void> {
		this.cancellation?.abort(new Error("grok-build: sign-in cancelled"));
		await this.operation?.catch(() => undefined);
		this.lastLoginError = undefined;
		this.codeResolver = undefined;
		await this.session.logout();
		this.state = { status: "signed-out", grokImportAvailable: await grokImportAvailable() };
		this.challenge = undefined;
	}

	async dispose(): Promise<void> {
		const failure = new Error("grok-build: plugin disposed");
		this.cancellation?.abort(failure);
		this.rejectChallenge(failure);
		await this.operation?.catch(() => undefined);
		this.lastLoginError = undefined;
		this.codeResolver = undefined;
		this.challenge = undefined;
	}

	private start(method: GrokBuildLoginMethod): void {
		this.lastLoginError = undefined;
		const cancellation = new AbortController();
		this.cancellation = cancellation;
		this.method = method;
		this.challenge = undefined;
		this.state = { status: "signing-in", method, grokImportAvailable: false };
		const run = method === "pkce" ? this.runPkce(cancellation) : this.runDevice(cancellation);
		this.operation = run
			.then(
				async () => {
					this.state = await this.readStoredStatus();
				},
				(error: unknown) => {
					this.rejectChallenge(error);
					this.lastLoginError = safeMessage(error);
					this.state = { status: "error", message: safeMessage(error), grokImportAvailable: false };
				},
			)
			.finally(() => {
				this.operation = undefined;
				this.cancellation = undefined;
				this.codeResolver = undefined;
			});
	}

	private async runPkce(cancellation: AbortController): Promise<void> {
		const persist = await this.session.store.prepareLoginPersist(this.loginPersist);
		const credential = await loginGrokBuildPkce({
			signal: cancellation.signal,
			onAuthorizeUrl: (url) => this.acceptChallenge({ method: "pkce", url }),
			awaitCode: (signal) =>
				new Promise<string>((resolve, reject) => {
					this.codeResolver = resolve;
					const onAbort = (): void => {
						this.codeResolver = undefined;
						reject(new Error("grok-build: sign-in cancelled"));
					};
					if (signal.aborted) {
						onAbort();
						return;
					}
					signal.addEventListener("abort", onAbort, { once: true });
				}),
		});
		await this.session.store.persistLoginCredential(credential, persist);
		this.session.notifyCredentialChange();
		await this.session.refreshLiveCatalog();
	}

	private async runDevice(cancellation: AbortController): Promise<void> {
		await loginGrokBuildSession(
			{
				signal: cancellation.signal,
				prompt: (prompt) =>
					prompt.type === "select"
						? Promise.resolve(
								prompt.options.some((option) => option.id === "oauth") ? "oauth" : (prompt.options[0]?.id ?? "oauth"),
							)
						: waitForPromptAbort(prompt),
				notify: (event) => {
					this.onEvent(event);
				},
			},
			this.session,
			this.loginPersist,
		);
	}

	private onEvent(event: AuthEvent): void {
		if (event.type === "device_code") {
			this.acceptChallenge({
				method: "device",
				url: event.verificationUri,
				...(event.userCode.length > 0 ? { userCode: event.userCode } : {}),
			});
			return;
		}
		if (event.type === "auth_url") {
			this.acceptChallenge({ method: this.method, url: event.url });
		}
	}

	private acceptChallenge(challenge: LoginChallenge): void {
		try {
			const url = new URL(challenge.url);
			if (url.protocol !== "https:") {
				const error = new Error("xAI returned an unsafe authorization URL");
				this.cancellation?.abort(error);
				this.rejectChallenge(error);
				return;
			}
		} catch {
			const error = new Error("xAI returned an invalid authorization URL");
			this.cancellation?.abort(error);
			this.rejectChallenge(error);
			return;
		}
		this.challenge = challenge;
		this.state = {
			status: "signing-in",
			method: challenge.method,
			url: challenge.url,
			grokImportAvailable: false,
			...(challenge.userCode === undefined ? {} : { userCode: challenge.userCode }),
		};
		for (const waiter of this.challengeWaiters.splice(0)) waiter.resolve(challenge);
	}

	private async readStoredStatus(): Promise<GrokBuildWebAuthStatus> {
		const [stored, grok] = await Promise.all([grokBuildAuthStatus(this.session.store), grokImportAvailable()]);
		if (!stored.authenticated) return { status: "signed-out", grokImportAvailable: grok };
		const available = this.session.availableModels().map((model) => model.id);
		const selected = this.session.selectedModelIds();
		const accounts = await signedInAccountFields(this.session.store);
		return {
			status: "signed-in",
			models: this.session.visibleModels().map((model) => model.id),
			available,
			selected: selected ?? available,
			selectionMode: this.session.selectedModelIds() === undefined ? "default" : "selected",
			catalogSource: this.session.catalogSource,
			grokImportAvailable: grok,
			...accounts,
			...(this.session.catalogError === undefined ? {} : { catalogError: this.session.catalogError }),
		};
	}

	private rejectChallenge(error: unknown): void {
		for (const waiter of this.challengeWaiters.splice(0)) waiter.reject(error);
	}
}

export type SubscriptionWebAuthStatus = {
	provider: Exclude<CodingOAuthProviderSlug, "grok">;
	route: string;
	displayName: string;
	loginMethods: readonly SubscriptionLoginMethod[];
	recommendedLoginMethod: SubscriptionLoginMethod;
	models: string[];
	available: string[];
	selected: string[];
	selectionMode?: "default" | "selected";
} & (
	| { status: "signed-out" }
	| { status: "signing-in"; method: SubscriptionLoginMethod; url?: string; userCode?: string }
	| { status: "signed-in"; expiresAt?: number; accounts: AccountSummary[]; activeAccountId: string }
	| { status: "error"; message: string }
);

export interface SubscriptionLoginChallenge {
	method: SubscriptionLoginMethod;
	url: string;
	userCode?: string;
}

function optionForLoginMethod(
	prompt: Extract<AuthPrompt, { type: "select" }>,
	method: SubscriptionLoginMethod,
): string {
	const exactIds =
		method === "device" ? ["device_code", "device-code", "device"] : ["browser", "browser_login", "browser-login"];
	for (const id of exactIds) {
		if (prompt.options.some((option) => option.id === id)) return id;
	}
	const label = method === "device" ? /device|headless/iu : /browser|pkce/iu;
	return prompt.options.find((option) => label.test(option.label))?.id ?? prompt.options[0]?.id ?? "";
}

/** Web lifecycle for one pi-ai subscription OAuth provider. */
export class SubscriptionWebAuth {
	private state: SubscriptionWebAuthStatus | undefined;
	private operation: Promise<void> | undefined;
	private lastLoginError: string | undefined;
	private cancellation: AbortController | undefined;
	private method: SubscriptionLoginMethod;
	private loginPersist: LoginPersistOptions = { mode: "add" };
	private challenge: SubscriptionLoginChallenge | undefined;
	private challengeWaiters: Array<{ resolve(value: SubscriptionLoginChallenge): void; reject(error: unknown): void }> =
		[];
	private codeResolver: ((code: string) => void) | undefined;

	constructor(
		readonly session: OAuthProviderSession,
		private readonly challengeTimeoutMs = 60_000,
	) {
		this.method = session.definition.recommendedLoginMethod;
	}

	async status(): Promise<SubscriptionWebAuthStatus & { operationError?: string }> {
		if (this.operation !== undefined && this.state !== undefined) return this.state;
		const stored = await this.readStoredStatus();
		return this.lastLoginError === undefined ? stored : { ...stored, operationError: this.lastLoginError };
	}

	async signIn(
		method: SubscriptionLoginMethod,
		persist: LoginPersistOptions = { mode: "add" },
	): Promise<SubscriptionLoginChallenge> {
		if (!this.session.definition.loginMethods.includes(method)) {
			throw new Error(`${this.session.definition.route}: login method "${method}" is not supported`);
		}
		if (this.operation !== undefined && this.method !== method) await this.cancel();
		if (
			this.operation !== undefined &&
			(this.loginPersist.mode !== persist.mode || this.loginPersist.targetAccountId !== persist.targetAccountId)
		)
			throw new Error("login already in progress for another account; cancel it first");
		if (this.operation === undefined) {
			this.loginPersist = { ...persist };
			this.start(method);
		}
		if (this.challenge !== undefined) return this.challenge;
		return new Promise<SubscriptionLoginChallenge>((resolve, reject) => {
			let timer: ReturnType<typeof setTimeout> | undefined;
			const waiter = {
				resolve: (challenge: SubscriptionLoginChallenge): void => {
					if (timer !== undefined) clearTimeout(timer);
					resolve(challenge);
				},
				reject: (error: unknown): void => {
					if (timer !== undefined) clearTimeout(timer);
					reject(error);
				},
			};
			this.challengeWaiters.push(waiter);
			timer = setTimeout(() => {
				const index = this.challengeWaiters.indexOf(waiter);
				if (index >= 0) this.challengeWaiters.splice(index, 1);
				const failure = new Error(`${this.session.definition.route}: timed out waiting for an OAuth challenge`);
				this.cancellation?.abort(failure);
				reject(failure);
			}, this.challengeTimeoutMs);
		});
	}

	async submitCode(code: string): Promise<void> {
		const resolver = this.codeResolver;
		if (resolver === undefined) {
			throw new Error(`${this.session.definition.route}: no authorization-code login is waiting for a code`);
		}
		this.codeResolver = undefined;
		resolver(code);
	}

	async cancel(): Promise<void> {
		this.cancellation?.abort(new Error(`${this.session.definition.route}: sign-in cancelled`));
		await this.operation?.catch(() => undefined);
		this.lastLoginError = undefined;
		this.codeResolver = undefined;
		this.challenge = undefined;
		this.state = await this.readStoredStatus();
	}

	async setModels(ids: readonly string[] | undefined): Promise<void> {
		await this.session.setSelectedModels(ids);
		this.state = await this.readStoredStatus();
	}

	async setActiveAccount(id: string): Promise<void> {
		await this.session.store.setActiveAccount(id);
		this.session.notifyCredentialChange();
		this.state = await this.readStoredStatus();
	}

	async removeAccount(id: string): Promise<void> {
		await this.session.store.removeAccount(id);
		this.session.notifyCredentialChange();
		this.state = await this.readStoredStatus();
	}

	async signOut(): Promise<void> {
		this.cancellation?.abort(new Error(`${this.session.definition.route}: sign-in cancelled`));
		await this.operation?.catch(() => undefined);
		this.lastLoginError = undefined;
		this.codeResolver = undefined;
		await this.session.logout();
		this.challenge = undefined;
		this.state = await this.readStoredStatus();
	}

	async dispose(): Promise<void> {
		this.cancellation?.abort(new Error(`${this.session.definition.route}: plugin disposed`));
		await this.operation?.catch(() => undefined);
		this.lastLoginError = undefined;
		this.codeResolver = undefined;
		this.rejectChallenge(new Error(`${this.session.definition.route}: plugin disposed`));
	}

	private baseStatus(): Omit<
		SubscriptionWebAuthStatus,
		"status" | "method" | "url" | "userCode" | "message" | "expiresAt" | "accounts" | "activeAccountId"
	> {
		const available = this.session.availableModels().map((model) => model.id);
		const selected = this.session.selectedModelIds() ?? available;
		return {
			provider: this.session.definition.slug,
			route: this.session.definition.route,
			displayName: this.session.definition.displayName,
			loginMethods: this.session.definition.loginMethods,
			recommendedLoginMethod: this.session.definition.recommendedLoginMethod,
			models: this.session.visibleModels().map((model) => model.id),
			available,
			selected,
			selectionMode: this.session.selectedModelIds() === undefined ? "default" : "selected",
		};
	}

	private async readStoredStatus(): Promise<SubscriptionWebAuthStatus> {
		const base = this.baseStatus();
		try {
			const stored = await this.session.status();
			if (!stored.authenticated) return { ...base, status: "signed-out" };
			const accounts = await signedInAccountFields(this.session.store);
			return {
				...base,
				status: "signed-in",
				...accounts,
				...(stored.expiresAt === undefined ? {} : { expiresAt: stored.expiresAt }),
			};
		} catch (error: unknown) {
			return { ...base, status: "error", message: safeMessage(error) };
		}
	}

	private start(method: SubscriptionLoginMethod): void {
		this.lastLoginError = undefined;
		const cancellation = new AbortController();
		this.cancellation = cancellation;
		this.method = method;
		this.challenge = undefined;
		this.state = { ...this.baseStatus(), status: "signing-in", method };
		this.operation = this.run(cancellation)
			.then(
				async () => {
					if (this.challenge === undefined)
						this.rejectChallenge(new Error(`${this.session.definition.route}: login completed without a challenge`));
					this.state = await this.readStoredStatus();
				},
				(error: unknown) => {
					this.rejectChallenge(error);
					this.lastLoginError = safeMessage(error);
					this.state = { ...this.baseStatus(), status: "error", message: safeMessage(error) };
				},
			)
			.finally(() => {
				this.operation = undefined;
				this.cancellation = undefined;
				this.codeResolver = undefined;
			});
	}

	private async run(cancellation: AbortController): Promise<void> {
		await this.session.login(
			{
				signal: cancellation.signal,
				prompt: (prompt) => {
					if (prompt.type === "select") return Promise.resolve(optionForLoginMethod(prompt, this.method));
					if (prompt.type === "manual_code") return this.awaitCode(prompt, cancellation.signal);
					return Promise.reject(new Error(`${this.session.definition.route}: unsupported OAuth prompt ${prompt.type}`));
				},
				notify: (event) => {
					this.onEvent(event);
				},
			},
			this.loginPersist,
		);
	}

	private awaitCode(prompt: AuthPrompt, cancellation: AbortSignal): Promise<string> {
		const signal = prompt.signal === undefined ? cancellation : AbortSignal.any([prompt.signal, cancellation]);
		return new Promise<string>((resolve, reject) => {
			const resolver = (code: string): void => {
				signal.removeEventListener("abort", onAbort);
				resolve(code);
			};
			const onAbort = (): void => {
				if (this.codeResolver === resolver) this.codeResolver = undefined;
				reject(signal.reason);
			};
			this.codeResolver = resolver;
			if (signal.aborted) {
				onAbort();
				return;
			}
			signal.addEventListener("abort", onAbort, { once: true });
		});
	}

	private onEvent(event: AuthEvent): void {
		if (event.type === "device_code") {
			this.acceptChallenge({
				method: "device",
				url: event.verificationUri,
				...(event.userCode.length > 0 ? { userCode: event.userCode } : {}),
			});
			return;
		}
		if (event.type === "auth_url") this.acceptChallenge({ method: this.method, url: event.url });
	}

	private acceptChallenge(challenge: SubscriptionLoginChallenge): void {
		try {
			const url = new URL(challenge.url);
			if (url.protocol !== "https:") throw new Error("authorization URL must use HTTPS");
		} catch (error) {
			const failure = new Error(`${this.session.definition.route}: invalid authorization URL`, { cause: error });
			this.cancellation?.abort(failure);
			this.rejectChallenge(failure);
			return;
		}
		this.challenge = challenge;
		this.state = {
			...this.baseStatus(),
			status: "signing-in",
			method: challenge.method,
			url: challenge.url,
			...(challenge.userCode === undefined ? {} : { userCode: challenge.userCode }),
		};
		for (const waiter of this.challengeWaiters.splice(0)) waiter.resolve(challenge);
	}

	private rejectChallenge(error: unknown): void {
		for (const waiter of this.challengeWaiters.splice(0)) waiter.reject(error);
	}
}

function json(res: ServerResponse, status: number, value: unknown): void {
	res.writeHead(status, {
		"content-type": "application/json; charset=utf-8",
		"cache-control": "no-store",
		"x-content-type-options": "nosniff",
	});
	res.end(JSON.stringify(value));
}

function readLoginMethod(body: unknown): GrokBuildLoginMethod {
	if (typeof body === "object" && body !== null && "method" in body && body.method === "device") return "device";
	return "pkce";
}

/** Register the plugin-owned OAuth routes when the Web server is composed. */
export function registerGrokBuildAuthRoutes(
	ctx: Context,
	session: GrokBuildSession,
	existingAuth?: GrokBuildWebAuth,
	ownerRequestPolicy: OwnerRequestPolicy = LOOPBACK_OWNER_REQUEST_POLICY,
): void {
	const auth = existingAuth ?? new GrokBuildWebAuth(session);
	const ownsAuth = existingAuth === undefined;
	if (ownsAuth) {
		ctx.effect(() => () => auth.dispose(), "dsh-coding-subscription-oauth: Grok OAuth auth lifetime");
	}
	ctx.effect(() => {
		const releaseRoutes = registerWebRouteSetupAtomically(ctx.webServer, (webServer) => [
			webServer.register({
				kind: "exact",
				path: GROK_BUILD_AUTH_STATUS_PATH,
				handler: async (req, res) => {
					if (req.method !== "GET") return json(res, 405, { error: "method not allowed" });
					if (!authorizeCodingOAuthRequest(req, ownerRequestPolicy).authorized)
						return json(res, 403, { error: "forbidden" });
					json(res, 200, await auth.status());
				},
			}),
			webServer.register({
				kind: "exact",
				path: GROK_BUILD_AUTH_LOGIN_PATH,
				handler: async (req, res) => {
					if (req.method !== "POST") return json(res, 405, { error: "method not allowed" });
					if (!authorizeCodingOAuthRequest(req, ownerRequestPolicy).authorized)
						return json(res, 403, { error: "forbidden" });
					try {
						json(res, 200, await auth.signIn(readLoginMethod(await readJsonRequest(req))));
					} catch (error: unknown) {
						json(res, requestErrorStatus(error, 500), { error: safeMessage(error) });
					}
				},
			}),
			webServer.register({
				kind: "exact",
				path: GROK_BUILD_AUTH_LOGIN_CODE_PATH,
				handler: async (req, res) => {
					if (req.method !== "POST") return json(res, 405, { error: "method not allowed" });
					if (!authorizeCodingOAuthRequest(req, ownerRequestPolicy).authorized)
						return json(res, 403, { error: "forbidden" });
					try {
						const body = await readJsonRequest(req);
						const code = typeof body === "object" && body !== null && "code" in body ? body.code : undefined;
						if (typeof code !== "string" || code.trim().length === 0) {
							return json(res, 400, { error: "code must be a non-empty string" });
						}
						await auth.submitCode(code);
						json(res, 200, { ok: true });
					} catch (error: unknown) {
						json(res, requestErrorStatus(error, 409), { error: safeMessage(error) });
					}
				},
			}),
			webServer.register({
				kind: "exact",
				path: GROK_BUILD_AUTH_LOGIN_CANCEL_PATH,
				handler: async (req, res) => {
					if (req.method !== "POST") return json(res, 405, { error: "method not allowed" });
					if (!authorizeCodingOAuthRequest(req, ownerRequestPolicy).authorized)
						return json(res, 403, { error: "forbidden" });
					await auth.cancel();
					json(res, 200, await auth.status());
				},
			}),
			webServer.register({
				kind: "exact",
				path: GROK_BUILD_AUTH_IMPORT_PATH,
				handler: (req, res) => {
					if (req.method !== "POST") return json(res, 405, { error: "method not allowed" });
					if (!authorizeCodingOAuthRequest(req, ownerRequestPolicy).authorized)
						return json(res, 403, { error: "forbidden" });
					json(res, 410, { error: "legacy import retired; use OAuth Pull preview and confirmation" });
				},
			}),
			webServer.register({
				kind: "exact",
				path: GROK_BUILD_AUTH_MODELS_PATH,
				handler: async (req, res) => {
					if (req.method !== "POST") return json(res, 405, { error: "method not allowed" });
					if (!authorizeCodingOAuthRequest(req, ownerRequestPolicy).authorized)
						return json(res, 403, { error: "forbidden" });
					try {
						const body = await readJsonRequest(req);
						const selected =
							typeof body === "object" && body !== null && "selected" in body ? body.selected : undefined;
						if (!Array.isArray(selected) || selected.some((id) => typeof id !== "string")) {
							return json(res, 400, { error: "selected must be an array of model ids" });
						}
						await auth.setModels(selected);
						json(res, 200, await auth.status());
					} catch (error: unknown) {
						json(res, requestErrorStatus(error, 500), { error: safeMessage(error) });
					}
				},
			}),
			webServer.register({
				kind: "exact",
				path: GROK_BUILD_AUTH_LOGOUT_PATH,
				handler: async (req, res) => {
					if (req.method !== "POST") return json(res, 405, { error: "method not allowed" });
					if (!authorizeCodingOAuthRequest(req, ownerRequestPolicy).authorized)
						return json(res, 403, { error: "forbidden" });
					await auth.signOut();
					json(res, 200, { ok: true });
				},
			}),
		]);
		return releaseRoutes;
	}, "dsh-coding-subscription-oauth: Web OAuth routes");
}

export interface CodingOAuthWebStatus {
	accessMode: OwnerAccessMode;
	compatibility: DshCompatibility;
	uiOwner: "hub" | "standalone";
	providers: {
		grok: GrokBuildWebAuthStatus;
		codex: SubscriptionWebAuthStatus;
		kimi: SubscriptionWebAuthStatus;
		claude: SubscriptionWebAuthStatus;
	};
	antigravity: {
		installed: boolean;
		route: typeof ANTIGRAVITY_ROUTE;
		management: "cli";
	};
	opencodeGo: {
		active: boolean;
		lastCall: "no-call" | "success" | "failure" | "missing-session";
		updatedAt: number | null;
	};
}

export interface CodingOAuthStatusContext {
	readonly uiOwner: "hub" | "standalone";
	compatibility(accessMode: OwnerAccessMode): DshCompatibility;
	opencodeGo?(): CodingOAuthWebStatus["opencodeGo"];
}

const DEFAULT_STATUS_CONTEXT: CodingOAuthStatusContext = {
	uiOwner: "hub",
	compatibility: (accessMode) => ({
		coreAbi: CODING_OAUTH_CORE_ABI,
		dshVersion: null,
		status: "degraded",
		uiOwner: "hub",
		accessMode,
		capabilities: {},
		diagnostics: ["host compatibility adapter is unavailable"],
	}),
	opencodeGo: () => ({ active: false, lastCall: "no-call", updatedAt: null }),
};

function recordBody(body: unknown): Record<string, unknown> {
	if (typeof body !== "object" || body === null || Array.isArray(body)) {
		throw new Error("request body must be an object");
	}
	return body as Record<string, unknown>;
}

function providerSlug(body: unknown): CodingOAuthProviderSlug {
	const provider = recordBody(body).provider;
	if (
		provider === "grok" ||
		provider === "codex" ||
		provider === "kimi" ||
		provider === "claude" ||
		provider === "copilot"
	) {
		return provider;
	}
	throw new Error("provider must be one of grok, codex, kimi, claude, or copilot");
}

/** Register the unified Coding OAuth API plus the compatibility Grok routes. */
export function registerCodingOAuthRoutes(
	ctx: Context,
	grokSession: GrokBuildSession,
	subscriptionSessions: readonly OAuthProviderSession[],
	ownerRequestPolicy: OwnerRequestPolicy = LOOPBACK_OWNER_REQUEST_POLICY,
	statusContext: CodingOAuthStatusContext = DEFAULT_STATUS_CONTEXT,
): void {
	const grok = new GrokBuildWebAuth(grokSession);
	const subscriptions = new Map(
		subscriptionSessions.map((session) => [session.definition.slug, new SubscriptionWebAuth(session)]),
	);
	const subscription = (slug: Exclude<CodingOAuthProviderSlug, "grok">): SubscriptionWebAuth => {
		const auth = subscriptions.get(slug);
		if (auth === undefined) throw new Error(`OAuth provider "${slug}" is not configured`);
		return auth;
	};
	ctx.effect(
		() => () =>
			Promise.all([grok.dispose(), ...[...subscriptions.values()].map((auth) => auth.dispose())]).then(() => undefined),
		"dsh-coding-subscription-oauth: Coding OAuth auth lifetime",
	);

	registerGrokBuildAuthRoutes(ctx, grokSession, grok, ownerRequestPolicy);

	const allStatus = async (accessMode: OwnerAccessMode): Promise<CodingOAuthWebStatus> => {
		const [grokStatus, codex, kimi, claude] = await Promise.all([
			grok.status().catch(
				async (error: unknown): Promise<GrokBuildWebAuthStatus> => ({
					status: "error",
					message: safeMessage(error),
					grokImportAvailable: await grokImportAvailable().catch(() => false),
				}),
			),
			subscription("codex").status(),
			subscription("kimi").status(),
			subscription("claude").status(),
		]);
		const copilotAuth = subscriptions.get("copilot");
		const copilot = copilotAuth === undefined ? undefined : await copilotAuth.status();
		let antigravityInstalled = false;
		try {
			const llm = ctx.get("llm") as { listProviders?(): readonly { id: string }[] } | undefined;
			antigravityInstalled = llm?.listProviders?.().some((provider) => provider.id === ANTIGRAVITY_ROUTE) ?? false;
		} catch {
			// Account cards remain usable even when an unrelated adapter list fails.
		}
		return {
			accessMode,
			compatibility: statusContext.compatibility(accessMode),
			uiOwner: statusContext.uiOwner,
			providers: {
				grok: grokStatus,
				codex,
				kimi,
				claude,
				...(copilot === undefined ? {} : { copilot }),
			},
			antigravity: {
				installed: antigravityInstalled,
				route: ANTIGRAVITY_ROUTE,
				management: "cli",
			},
			opencodeGo: statusContext.opencodeGo?.() ?? { active: false, lastCall: "no-call", updatedAt: null },
		};
	};

	ctx.effect(() => {
		const releaseRoutes = registerWebRouteSetupAtomically(ctx.webServer, (webServer) => [
			webServer.register({
				kind: "exact",
				path: CODING_OAUTH_STATUS_PATH,
				handler: async (req, res) => {
					if (req.method !== "GET") return json(res, 405, { error: "method not allowed" });
					const decision = authorizeCodingOAuthRequest(req, ownerRequestPolicy);
					if (!decision.authorized || decision.accessMode === undefined) return json(res, 403, { error: "forbidden" });
					try {
						json(res, 200, await allStatus(decision.accessMode));
					} catch (error: unknown) {
						json(res, requestErrorStatus(error, 500), { error: safeMessage(error) });
					}
				},
			}),
			webServer.register({
				kind: "exact",
				path: CODING_OAUTH_LOGIN_PATH,
				handler: async (req, res) => {
					if (req.method !== "POST") return json(res, 405, { error: "method not allowed" });
					if (!authorizeCodingOAuthRequest(req, ownerRequestPolicy).authorized)
						return json(res, 403, { error: "forbidden" });
					try {
						const body = await readJsonRequest(req);
						const slug = providerSlug(body);
						const value = recordBody(body);
						const persist = readLoginPersistOptions(value);
						if (slug === "grok") {
							const method: GrokBuildLoginMethod = value.method === "device" ? "device" : "pkce";
							return json(res, 200, await grok.signIn(method, persist));
						}
						const auth = subscription(slug);
						const requested = value.method;
						const method: SubscriptionLoginMethod =
							requested === "browser" || requested === "device"
								? requested
								: auth.session.definition.recommendedLoginMethod;
						json(res, 200, await auth.signIn(method, persist));
					} catch (error: unknown) {
						json(res, requestErrorStatus(error, 500), { error: safeMessage(error) });
					}
				},
			}),
			webServer.register({
				kind: "exact",
				path: CODING_OAUTH_LOGIN_CODE_PATH,
				handler: async (req, res) => {
					if (req.method !== "POST") return json(res, 405, { error: "method not allowed" });
					if (!authorizeCodingOAuthRequest(req, ownerRequestPolicy).authorized)
						return json(res, 403, { error: "forbidden" });
					try {
						const body = await readJsonRequest(req);
						const slug = providerSlug(body);
						const code = recordBody(body).code;
						if (typeof code !== "string" || code.trim().length === 0) {
							return json(res, 400, { error: "code must be a non-empty string" });
						}
						if (slug === "grok") await grok.submitCode(code);
						else await subscription(slug).submitCode(code);
						json(res, 200, { ok: true });
					} catch (error: unknown) {
						json(res, requestErrorStatus(error, 409), { error: safeMessage(error) });
					}
				},
			}),
			webServer.register({
				kind: "exact",
				path: CODING_OAUTH_LOGIN_CANCEL_PATH,
				handler: async (req, res) => {
					if (req.method !== "POST") return json(res, 405, { error: "method not allowed" });
					const decision = authorizeCodingOAuthRequest(req, ownerRequestPolicy);
					if (!decision.authorized || decision.accessMode === undefined) return json(res, 403, { error: "forbidden" });
					try {
						const body = await readJsonRequest(req);
						const slug = providerSlug(body);
						if (slug === "grok") await grok.cancel();
						else await subscription(slug).cancel();
						json(res, 200, await allStatus(decision.accessMode));
					} catch (error: unknown) {
						json(res, requestErrorStatus(error, 500), { error: safeMessage(error) });
					}
				},
			}),
			webServer.register({
				kind: "exact",
				path: CODING_OAUTH_MODELS_PATH,
				handler: async (req, res) => {
					if (req.method !== "POST") return json(res, 405, { error: "method not allowed" });
					const decision = authorizeCodingOAuthRequest(req, ownerRequestPolicy);
					if (!decision.authorized || decision.accessMode === undefined) return json(res, 403, { error: "forbidden" });
					try {
						const body = await readJsonRequest(req);
						const slug = providerSlug(body);
						const selected = recordBody(body).selected;
						if (!Array.isArray(selected) || selected.some((id) => typeof id !== "string")) {
							return json(res, 400, { error: "selected must be an array of model ids" });
						}
						if (slug === "grok")
							await grok.setModels(recordBody(body).selectionMode === "default" ? undefined : selected);
						else
							await subscription(slug).setModels(recordBody(body).selectionMode === "default" ? undefined : selected);
						json(res, 200, await allStatus(decision.accessMode));
					} catch (error: unknown) {
						json(res, requestErrorStatus(error, 500), { error: safeMessage(error) });
					}
				},
			}),
			webServer.register({
				kind: "exact",
				path: CODING_OAUTH_LOGOUT_PATH,
				handler: async (req, res) => {
					if (req.method !== "POST") return json(res, 405, { error: "method not allowed" });
					const decision = authorizeCodingOAuthRequest(req, ownerRequestPolicy);
					if (!decision.authorized || decision.accessMode === undefined) return json(res, 403, { error: "forbidden" });
					try {
						const body = await readJsonRequest(req);
						const slug = providerSlug(body);
						if (slug === "grok") await grok.signOut();
						else await subscription(slug).signOut();
						json(res, 200, await allStatus(decision.accessMode));
					} catch (error: unknown) {
						json(res, requestErrorStatus(error, 500), { error: safeMessage(error) });
					}
				},
			}),
			webServer.register({
				kind: "exact",
				path: CODING_OAUTH_ACCOUNTS_SET_ACTIVE_PATH,
				handler: async (req, res) => {
					if (req.method !== "POST") return json(res, 405, { error: "method not allowed" });
					const decision = authorizeCodingOAuthRequest(req, ownerRequestPolicy);
					if (!decision.authorized || decision.accessMode === undefined) return json(res, 403, { error: "forbidden" });
					try {
						const body = recordBody(await readJsonRequest(req));
						const slug = providerSlug(body);
						const accountId = body.accountId;
						if (typeof accountId !== "string" || accountId.trim().length === 0) {
							return json(res, 400, { error: "accountId must be a non-empty string" });
						}
						if (slug === "grok") await grok.setActiveAccount(accountId);
						else await subscription(slug).setActiveAccount(accountId);
						json(res, 200, await allStatus(decision.accessMode));
					} catch (error: unknown) {
						json(res, requestErrorStatus(error, 500), { error: safeMessage(error) });
					}
				},
			}),
			webServer.register({
				kind: "exact",
				path: CODING_OAUTH_ACCOUNTS_REMOVE_PATH,
				handler: async (req, res) => {
					if (req.method !== "POST") return json(res, 405, { error: "method not allowed" });
					const decision = authorizeCodingOAuthRequest(req, ownerRequestPolicy);
					if (!decision.authorized || decision.accessMode === undefined) return json(res, 403, { error: "forbidden" });
					try {
						const body = recordBody(await readJsonRequest(req));
						const slug = providerSlug(body);
						const accountId = body.accountId;
						if (typeof accountId !== "string" || accountId.trim().length === 0) {
							return json(res, 400, { error: "accountId must be a non-empty string" });
						}
						if (slug === "grok") await grok.removeAccount(accountId);
						else await subscription(slug).removeAccount(accountId);
						json(res, 200, await allStatus(decision.accessMode));
					} catch (error: unknown) {
						json(res, requestErrorStatus(error, 500), { error: safeMessage(error) });
					}
				},
			}),
		]);
		return releaseRoutes;
	}, "dsh-coding-subscription-oauth: Coding OAuth routes");
}
